import React, { useState, useEffect } from 'react';
import { Box, Grid, IconButton, Table, TableHead, TableRow, TableCell, TableBody, Alert, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import InvButton from '../../components/InvButton';
import { InvTextField, InvSelect } from '../../components/InvField';
import { createReturn, getIssues } from '../../api/dispensing';
import { getStores } from '../../api/stockLedger';
import useItemNames from '../../hooks/useItemNames';
import { resolveDispensingScope, resolveIssueId, formatDispensingError } from '../../utils/dispensingEntity';
import { generateTxnNumber } from '../../utils';
import { useEmrContext } from '../../hooks/useEmrContext';
import { RETURN_TYPES } from '../../constants';
import { IPD_COLORS } from '../../theme';

const LINE_DEFAULT = { item_id: '', batch_number: '', expiry_date: '', requested_quantity: '', reason: '' };

export default function ReturnForm() {
  const history = useHistory();
  const { url } = useRouteMatch();
  const emr = useEmrContext();
  const { items: allItems, itemOptionLabel: labelItem } = useItemNames();
  const [saving, setSaving] = useState(false);
  const [stores, setStores] = useState([]);
  const [issues, setIssues] = useState([]);
  const [lines, setLines] = useState([{ ...LINE_DEFAULT }]);
  const [error, setError] = useState(null);
  const [issuesWarning, setIssuesWarning] = useState(null);

  const { control, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      transaction_number: generateTxnNumber('RET'),
      return_type: 'PATIENT_RETURN',
      to_store_id: '', patient_id: '', source_reference_id: '',
      department_id: emr.department_id,
      ward_id: emr.ward_id,
      transaction_date: dayjs().format('YYYY-MM-DD'),
      remarks: '',
    },
  });

  useEffect(() => {
    // API: GET /inventory/stores/
    getStores().then(d => setStores(Array.isArray(d) ? d : (d?.items || [])))
      .catch(e => console.error('[ReturnForm] load stores failed:', e));
    // Only ACKNOWLEDGED / COMPLETED issues have stock at the receiver — ISSUED is still in transit.
    Promise.all([
      getIssues({ status: 'ACKNOWLEDGED' }),
      getIssues({ status: 'COMPLETED' }),
    ]).then(([ack, done]) => {
      const a = Array.isArray(ack) ? ack : (ack?.items || ack?.data || []);
      const b = Array.isArray(done) ? done : (done?.items || done?.data || []);
      const byId = new Map();
      [...a, ...b].forEach(row => {
        const id = resolveIssueId(row);
        if (id != null) byId.set(String(id), row);
      });
      setIssues([...byId.values()]);
      setIssuesWarning(null);
    }).catch(e => {
      console.error('[ReturnForm] load issues failed:', e);
      setIssues([]);
      setIssuesWarning(
        formatDispensingError(e, 'Could not load acknowledged issues for the Source Issue picker.'),
      );
    });
  }, []);

  // A line with no item picked is just an untouched blank row — skip it silently.
  // A line with an item picked but a bad quantity is a mistake — block the save
  // instead of silently dropping it from the return.
  function invalidLineMessage(line) {
    if (!line.item_id) return null;
    if (!(Number(line.requested_quantity) > 0)) {
      return 'One of the item rows has an item selected but no valid quantity. Fix the quantity or remove the row before saving.';
    }
    return null;
  }

  async function onSubmit(data) {
    setError(null);
    for (const line of lines) {
      const msg = invalidLineMessage(line);
      if (msg) { setError(msg); return; }
    }
    const validLines = lines.filter(l => l.item_id && Number(l.requested_quantity) > 0);
    if (!validLines.length) {
      setError('Add at least one item to return.');
      return;
    }
    setSaving(true);
    try {
      const { department_id, ward_id, ...rest } = data;
      // API: POST /inventory/dispensing/returns/
      await createReturn({
        ...rest,
        ...(department_id ? { department_id } : {}),
        ...(ward_id ? { ward_id } : {}),
        admission_id: emr.external_admission_id || undefined,
        source_reference_id: data.source_reference_id || undefined,
        items: validLines.map(l => {
          const item = allItems.find(i => (i.item_id || i.id) === l.item_id);
          return {
            item_id: l.item_id,
            uom_id: item?.base_uom_id || undefined,
            batch_number: l.batch_number || undefined,
            expiry_date: l.expiry_date || undefined,
            requested_quantity: Number(l.requested_quantity),
            reason: l.reason || undefined,
          };
        }),
      }, resolveDispensingScope({ to_store_id: data.to_store_id }, stores));
      history.push(`${url.replace('/return/new', '/returns')}`);
    } catch (e) {
      console.error('[ReturnForm] create return failed:', e);
      setError(formatDispensingError(e, 'Failed to create return'));
    } finally { setSaving(false); }
  }

  const storeOptions = stores.map(s => ({ value: s.store_id || s.id, label: s.store_name }));
  const itemOptions = allItems.map(i => ({ value: i.item_id || i.id, label: labelItem(i) }));
  const issueOptions = issues.map(i => ({
    value: resolveIssueId(i),
    label: i.transaction_number || resolveIssueId(i),
  }));

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <PageHeader eyebrow="Inventory Management" title="New Return"
        actions={
          <>
            <InvButton variant="outlined" tone="teal" onClick={() => history.goBack()}
              sx={{ width: { xs: '100%', sm: 'auto' } }}>Cancel</InvButton>
            <InvButton type="submit" loading={saving}
              sx={{ width: { xs: '100%', sm: 'auto' } }}>Create Return</InvButton>
          </>
        }
      />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {issuesWarning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setIssuesWarning(null)}>
          {issuesWarning}
        </Alert>
      )}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={8}>
          <SectionCard title="Return Details">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller name="transaction_number" control={control} rules={{ required: 'Required' }}
                  render={({ field }) => <InvTextField {...field} label="Return *" error={errors.transaction_number} />} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller name="return_type" control={control}
                  render={({ field }) => (
                    <InvSelect label="Return Type" value={field.value} onChange={field.onChange}
                      options={RETURN_TYPES} />
                  )} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller name="to_store_id" control={control} rules={{ required: 'Required' }}
                  render={({ field }) => (
                    <InvSelect label="Return To Store *" value={field.value} onChange={field.onChange}
                      options={storeOptions} error={errors.to_store_id} />
                  )} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller name="patient_id" control={control}
                  render={({ field }) => <InvTextField {...field} label="Patient ID (optional)" />} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller name="source_reference_id" control={control}
                  render={({ field }) => (
                    <InvSelect label="Source Issue (optional)" value={field.value} onChange={field.onChange}
                      options={issueOptions} />
                  )} />
              </Grid>
              {emr.external_admission_id && (
                <Grid item xs={12} sm={4}>
                  <Box sx={{ px: 1.5, py: 1, border: `1px solid ${IPD_COLORS.border}`, borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ color: IPD_COLORS.textSecondary, display: 'block' }}>Admission</Typography>
                    <Typography sx={{ color: IPD_COLORS.textPrimary, fontSize: 14 }}>{emr.external_admission_id}</Typography>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} sm={4}>
                {emr.department_id ? (
                  <Box sx={{ px: 1.5, py: 1, border: `1px solid ${IPD_COLORS.border}`, borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ color: IPD_COLORS.textSecondary, display: 'block' }}>Department</Typography>
                    <Typography sx={{ color: IPD_COLORS.textPrimary, fontSize: 14 }}>{emr.department_id}</Typography>
                  </Box>
                ) : (
                  <Controller name="department_id" control={control}
                    render={({ field }) => <InvTextField {...field} label="Department ID (optional)" placeholder="UUID" />} />
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                {emr.ward_id ? (
                  <Box sx={{ px: 1.5, py: 1, border: `1px solid ${IPD_COLORS.border}`, borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ color: IPD_COLORS.textSecondary, display: 'block' }}>Ward</Typography>
                    <Typography sx={{ color: IPD_COLORS.textPrimary, fontSize: 14 }}>{emr.ward_id}</Typography>
                  </Box>
                ) : (
                  <Controller name="ward_id" control={control}
                    render={({ field }) => <InvTextField {...field} label="Ward ID (optional)" placeholder="UUID" />} />
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                <Controller name="transaction_date" control={control}
                  render={({ field }) => <InvTextField {...field} label="Date" type="date" InputLabelProps={{ shrink: true }} />} />
              </Grid>
              <Grid item xs={12}>
                <Controller name="remarks" control={control}
                  render={({ field }) => <InvTextField {...field} label="Remarks" multiline rows={2} />} />
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard title="Items Being Returned" sx={{ mt: 2.5 }}
            actions={<InvButton size="small" startIcon={<AddIcon />} onClick={() => setLines(l => [...l, { ...LINE_DEFAULT }])}>Add Row</InvButton>}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, minWidth: 220 }}>Item</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 130 }}>Batch </TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 150 }}>Expiry</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary, width: 100 }}>Qty</TableCell>
                    <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Reason</TableCell>
                    <TableCell sx={{ width: 50 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <InvSelect label="" value={line.item_id}
                          onChange={e => setLines(l => l.map((r, idx) => idx === i ? { ...r, item_id: e.target.value } : r))} options={itemOptions} />
                      </TableCell>
                      <TableCell>
                        <InvTextField value={line.batch_number} label=""
                          onChange={e => setLines(l => l.map((r, idx) => idx === i ? { ...r, batch_number: e.target.value } : r))} />
                      </TableCell>
                      <TableCell>
                        <InvTextField value={line.expiry_date} label="" type="date"
                          InputLabelProps={{ shrink: true }}
                          onChange={e => setLines(l => l.map((r, idx) => idx === i ? { ...r, expiry_date: e.target.value } : r))} />
                      </TableCell>
                      <TableCell>
                        <InvTextField value={line.requested_quantity} label="" type="number"
                          inputProps={{ min: 0.01, step: 0.01 }}
                          onChange={e => setLines(l => l.map((r, idx) => idx === i ? { ...r, requested_quantity: e.target.value } : r))} />
                      </TableCell>
                      <TableCell>
                        <InvTextField value={line.reason} label=""
                          onChange={e => setLines(l => l.map((r, idx) => idx === i ? { ...r, reason: e.target.value } : r))} />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => setLines(l => l.filter((_, idx) => idx !== i))} sx={{ color: IPD_COLORS.error.text }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
